/**
 * File: assets/breed-modal.ui.js
 * UI logic:
 * - builds DOM
 * - binds events
 * - calls core for data + fetching
 * - renders cards
 */
(() => {
  const NS = '[BreedModal/UI]';
  const log = (...args) => console.log(NS, ...args);

  /* I18N-06 (#163): visible strings come from the page; the English literal is the fallback. */
  const wfStrings = (() => {
    try {
      const node = document.querySelector('script[type="application/json"][data-wf-i18n="breed-modal"]');
      const parsed = node ? JSON.parse(node.textContent) : null;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      return {};
    }
  })();
  // Shopify's `t` HTML-escapes every translation whose key does not end in `_html` (French
  // "n'a" arrives as "n&#39;a"), so the page's values are decoded back to plain text.
  const wfDecode = (text) =>
    text.replace(/&(#x[0-9a-f]+|#\d+|amp|lt|gt|quot|apos);/gi, (entity, body) => {
      const named = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" }[body.toLowerCase()];
      if (named) return named;
      const code = body[1] === 'x' || body[1] === 'X' ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
      return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : entity;
    });
  const tr = (key, fallback, values = {}) => {
    const text = typeof wfStrings[key] === 'string' && wfStrings[key] !== '' ? wfDecode(wfStrings[key]) : fallback;
    return Object.keys(values).reduce((out, name) => out.split(`{${name}}`).join(values[name]), text);
  };

  function escapeHtml(s) {
    return String(s)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function buildModalDOM({ heroImg }) {
    const overlay = document.createElement('div');
    overlay.className = 'breed-modal-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    /* I18N-06 (#163): this dialog's text and labels come from the bridge, in the page's language;
       what goes into the innerHTML below is escaped first, so a translation never becomes markup.
       The hidden "No products found for this size." line stays: createUI replaces it before the
       dialog can open. */
    overlay.setAttribute('aria-label', tr('dialog_label', 'Find your dog breed'));

    /* A11Y-20 (#302): the search box in this template carries aria-label="Search dog breeds" - it
       was the first control focused in the dialog and had a placeholder only */
    overlay.innerHTML = `
      <div class="breed-modal" role="document">
        <button class="breed-modal__close" type="button" aria-label="${escapeHtml(tr('close', 'Close'))}">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M6 6L18 18" stroke="#150F1D" stroke-width="2" stroke-linecap="round"/>
            <path d="M18 6L6 18" stroke="#150F1D" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>

        <div class="breed-modal__content">
          <div class="breed-modal__hero">
            ${
              heroImg
                ? `<img class="breed-modal__hero-img" src="${escapeHtml(heroImg)}" alt="" loading="lazy">`
                : `<div class="breed-modal__hero-placeholder"></div>`
            }
          </div>

          <div class="breed-modal__title">${/* I18N-06 (#163) */ escapeHtml(tr('title', 'Shop perfectly for your furry friend'))}</div>
          <div class="breed-modal__desc">
            ${escapeHtml(tr('description', 'Tell us your dog\'s breed, and we’ll instantly find the ideal bed size, leash length, and gear tailored specifically for them.'))}
          </div>

          <div class="breed-modal__search-wrap">
            <input class="breed-modal__search" type="text" placeholder="${escapeHtml(tr('search_placeholder', 'Start typing a breed...'))}" aria-label="${escapeHtml(tr('search_label', 'Search dog breeds'))}" autocomplete="off" />
            <button class="breed-modal__clear" type="button" aria-label="${escapeHtml(tr('clear', 'Clear'))}">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M6 6L18 18" stroke="#150F1D" stroke-width="2" stroke-linecap="round"/>
                <path d="M18 6L6 18" stroke="#150F1D" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
          </div>

          <div class="breed-modal__list" role="listbox" aria-label="${/* I18N-06 (#163) */ escapeHtml(tr('list_label', 'Breed options'))}"></div>

          <div class="breed-modal__results" aria-live="polite">
            <div class="breed-modal__tabs" data-results-tabs hidden></div>

            <div class="breed-modal__results-body">
              <div class="breed-modal__results-empty" hidden>No products found for this size.</div>
              <div class="breed-modal__results-grid" data-results-grid></div>
            </div>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);
    return overlay;
  }

  function buildFloatingButton({ floatBtnId, iconUrl }) {
    if (document.getElementById(floatBtnId)) return null;

    const btn = document.createElement('button');
    btn.id = floatBtnId;
    btn.type = 'button';
    btn.className = 'woopf-breed-float';
    /* I18N-06 (#163): the floating button's name, in the page's language */
    btn.setAttribute('aria-label', tr('open_label', 'Choose dog breed'));
    /* A11Y-20 (#302): the button opens a dialog and reports whether it is open */
    btn.setAttribute('aria-haspopup', 'dialog');
    btn.setAttribute('aria-expanded', 'false');
    btn.innerHTML = `<img class="woopf-breed-float__icon" src="${escapeHtml(iconUrl)}" alt="" aria-hidden="true" />`;

    document.body.appendChild(btn);
    return btn;
  }

  function setResultsLoading(overlay) {
    const gridHost = overlay.querySelector('[data-results-grid]');
    const emptyEl = overlay.querySelector('.breed-modal__results-empty');
    const tabsHost = overlay.querySelector('[data-results-tabs]');

    if (tabsHost) {
      tabsHost.hidden = true;
      tabsHost.innerHTML = '';
    }

    if (emptyEl) emptyEl.hidden = true;

    if (gridHost) {
      /* I18N-06 (#163): the two loading lines, translated and escaped */
      gridHost.innerHTML = `
        <div class="breed-modal__loading">
          <div class="breed-modal__spinner" aria-hidden="true"></div>
          <div class="breed-modal__loading-text">${escapeHtml(tr('loading', 'Finding the best picks for your dog…'))}</div>
          <div class="breed-modal__loading-subtext">${escapeHtml(tr('loading_detail', 'Checking beds, walking gear, and enrichment products.'))}</div>
        </div>
      `;
    }
  }

  function setResultsEmpty(overlay, message) {
    const gridHost = overlay.querySelector('[data-results-grid]');
    const emptyEl = overlay.querySelector('.breed-modal__results-empty');

    if (gridHost) gridHost.innerHTML = '';
    if (emptyEl) {
      emptyEl.textContent = message || 'No beds found for this size.';
      emptyEl.hidden = false;
    }
  }

  function resetResults(overlay, message) {
    const tabsHost = overlay.querySelector('[data-results-tabs]');
    const gridHost = overlay.querySelector('[data-results-grid]');
    const emptyEl = overlay.querySelector('.breed-modal__results-empty');

    if (tabsHost) {
      tabsHost.hidden = true;
      tabsHost.innerHTML = '';
    }

    if (gridHost) {
      gridHost.innerHTML = '';
    }

    if (emptyEl) {
      /* I18N-06 (#163): the prompt, translated */
      emptyEl.textContent = message || tr('prompt', 'Choose a breed to see recommended products.');
      emptyEl.hidden = false;
    }
  }

  function renderList(listEl, items) {
    if (!listEl) return;
    if (!items.length) {
      /* I18N-06 (#163): translated and escaped; the breed names themselves are data and stay as they are */
      listEl.innerHTML = `<div class="breed-modal__empty">${escapeHtml(tr('no_breeds', 'No breeds found.'))}</div>`;
      return;
    }
    listEl.innerHTML = items
      .map(
        (b) =>
          `<div class="breed-modal__item" role="option" data-breed="${escapeHtml(b)}">${escapeHtml(
            b
          )}</div>`
      )
      .join('');
  }

  /* I18N-06 (#163): display text only - beds, gear and toys stay the data keys. The stylesheet
     upper-cases the chip, so the English reads as before. 'RECOMMENDED' stays English: the core
     only ever hands over beds, gear and toys, each with its own label, so it is never shown. */
  function getSourceLabel(key) {
    if (key === 'beds') return tr('category_beds', 'Sleep & Comfort');
    if (key === 'gear') return tr('category_gear', 'Walking Gear');
    if (key === 'toys') return tr('category_toys', 'Play & Enrichment');
    return 'RECOMMENDED';
  }

  function renderCard(p) {
    /* I18N-06 (#163): the fallback title and, below, the button label, translated */
    const title = (p.title || tr('view_product', 'View product')).trim();
    const url = p.url || '#';
    const img = p.img || '';
    const price = (p.price || '').trim();

    return `
      <div class="bm-card" data-source-key="${escapeHtml(p.sourceKey || '')}">
        <a class="bm-card__media" href="${escapeHtml(url)}" aria-label="${escapeHtml(title)}">
          ${
            img
              ? `<img class="bm-card__img" src="${escapeHtml(img)}" alt="${escapeHtml(title)}" loading="lazy">`
              : `<div class="bm-card__img bm-card__img--ph"></div>`
          }
        </a>

        <div class="bm-card__body">
          <div class="bm-card__tag">${escapeHtml(p.sourceLabel || getSourceLabel(p.sourceKey))}</div>

          <a class="bm-card__title" href="${escapeHtml(url)}">
            ${escapeHtml(title)}
          </a>

          ${price ? `<div class="bm-card__price">${escapeHtml(price)}</div>` : ``}

          <div class="bm-card__cta">
            <a class="bm-card__btn" href="${escapeHtml(url)}">${/* I18N-06 (#163) */ escapeHtml(tr('buy_now', 'Buy Now'))}</a>
          </div>
        </div>
      </div>
    `;
  }

  function renderResultsCards(overlay, products) {
    const gridHost = overlay.querySelector('[data-results-grid]');
    const tabsHost = overlay.querySelector('[data-results-tabs]');
    if (!gridHost) return;

    const allProducts = Array.isArray(products) ? products.slice(0, 24) : [];

    const counts = allProducts.reduce(
      (acc, p) => {
        const key = p.sourceKey || 'other';
        acc.all += 1;
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      },
      { all: 0 }
    );

    /* I18N-06 (#163): the tab labels, translated; the keys stay the data, and the stylesheet
       upper-cases the chips */
    const availableTabs = [
      { key: 'all', label: tr('tab_all', 'All') },
      { key: 'beds', label: tr('category_beds', 'Sleep & Comfort') },
      { key: 'gear', label: tr('category_gear', 'Walking Gear') },
      { key: 'toys', label: tr('category_toys', 'Play & Enrichment') },
    ].filter((tab) => counts[tab.key]);

    if (tabsHost) {
      tabsHost.hidden = availableTabs.length <= 1;
      tabsHost.innerHTML = availableTabs
        .map(
          (tab, index) => `
            <button
              class="breed-modal__tab ${index === 0 ? 'is-active' : ''}"
              type="button"
              data-tab-key="${escapeHtml(tab.key)}"
            >
              ${escapeHtml(tab.label)} (${counts[tab.key]})
            </button>
          `
        )
        .join('');
    }

    function renderByTab(tabKey) {
      const filtered =
        tabKey === 'all'
          ? allProducts
          : allProducts.filter((p) => (p.sourceKey || 'other') === tabKey);

      gridHost.innerHTML = `<div class="bm-cards">${filtered.map(renderCard).join('')}</div>`;
    }

    renderByTab('all');

    tabsHost?.querySelectorAll('[data-tab-key]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const tabKey = btn.getAttribute('data-tab-key') || 'all';

        tabsHost.querySelectorAll('.breed-modal__tab').forEach((x) => {
          x.classList.remove('is-active');
        });

        btn.classList.add('is-active');
        renderByTab(tabKey);
      });
    });
  }

  // Scroll lock copied from your working setup
  let locked = false;
  let prevOverflow = '';
  let prevPaddingRight = '';
  let prevScrollY = 0;

  function lockBodyScroll() {
    if (locked) return;
    locked = true;

    prevOverflow = document.body.style.overflow || '';
    prevPaddingRight = document.body.style.paddingRight || '';
    prevScrollY = window.scrollY || 0;

    const scrollBarW = window.innerWidth - document.documentElement.clientWidth;
    if (scrollBarW > 0) document.body.style.paddingRight = `${scrollBarW}px`;

    document.body.style.overflow = 'hidden';
  }

  function unlockBodyScroll() {
    if (!locked) return;
    locked = false;

    document.body.style.overflow = prevOverflow;
    document.body.style.paddingRight = prevPaddingRight;
    try {
      window.scrollTo(0, prevScrollY);
    } catch {}
  }

  function openModal(overlay, core) {
    /* A11Y-20 (#302), global constraint: never CDialog.open a detached root. The overlay is a plain
       node built once and never removed, but guard it like every other CDialog consumer. Strictly
       false: isConnected is undefined on the test double built for these tests, which is not
       detached. */
    if (overlay.isConnected === false) return;

    overlay.classList.add('is-open');
    lockBodyScroll();

    /* A11Y-20 (#302): the floating button reports the dialog open, and assets/c-dialog.js makes the
       page behind inert and hands focus back to the button (or wherever focus was) on close. The
       search box still takes focus itself, 50 ms below. */
    const floatBtn = document.querySelector('.woopf-breed-float');
    floatBtn?.setAttribute('aria-expanded', 'true');
    window.CDialog?.open({ root: overlay, returnFocus: document.activeElement, fallbackFocus: floatBtn });

    const input = overlay.querySelector('.breed-modal__search');
    const clearBtn = overlay.querySelector('.breed-modal__clear');

    if (input) {
      const prev = core.getSelectedBreed();

      if (prev) {
        input.value = prev;
        clearBtn?.classList.add('is-visible');

        // re-fetch on open
        if (typeof overlay.__applyBreed === 'function') {
          overlay.__applyBreed(prev, { persist: false });
        }
      } else {
        clearBtn?.classList.remove('is-visible');
        /* I18N-06 (#163) */
        resetResults(overlay, tr('prompt', 'Choose a breed to see recommended products.'));
      }

      setTimeout(() => input.focus(), 50);
    }
  }

  function closeModal(overlay, core) {
    overlay.classList.remove('is-open');
    unlockBodyScroll();
    core.markShown();
    /* A11Y-20 (#302): report the dialog closed; c-dialog.js releases the page and returns focus to
       the button instead of leaving it on <body> */
    document.querySelector('.woopf-breed-float')?.setAttribute('aria-expanded', 'false');
    window.CDialog?.close(overlay);
  }

  function bindEvents(overlay, core, breedsSorted) {
    const closeBtn = overlay.querySelector('.breed-modal__close');
    const input = overlay.querySelector('.breed-modal__search');
    const clearBtn = overlay.querySelector('.breed-modal__clear');
    const listEl = overlay.querySelector('.breed-modal__list');

    const indexed = breedsSorted.map((b) => ({ raw: b, key: core.normSearch(b) }));

    const update = () => {
      const qRaw = input?.value || '';
      const q = core.normSearch(qRaw);
      const isTyping = q.length > 0;

      clearBtn?.classList.toggle('is-visible', isTyping);

      if (!isTyping) {
        listEl?.classList.remove('is-open');
        if (listEl) listEl.innerHTML = '';
        return;
      }

      // GAP3-11 (#226): Cavoodle, Groodle, Spoodle, Moodle, Blue Heeler and Koolie returned
      // nothing, and Staffy returned only Staffy Bull Bullmastiff - a rare crossbreed - because
      // the Staffordshire Bull Terrier is not spelled with a "y". Alias targets are matched
      // exactly and ADDED to the substring search, so nothing that used to be found is lost.
      const aliasKeys = (
        typeof window.WOOPF_BREED_ALIAS_TARGETS === 'function'
          ? window.WOOPF_BREED_ALIAS_TARGETS(qRaw)
          : []
      ).map((name) => core.normSearch(name));

      const out = [];
      for (let i = 0; i < indexed.length; i++) {
        const it = indexed[i];
        if (it.key.includes(q) || aliasKeys.includes(it.key)) out.push(it.raw);
        if (out.length >= 60) break;
      }

      renderList(listEl, out);
      listEl?.classList.add('is-open');
    };

    async function applyBreed(breed, { persist = true } = {}) {
      const b = (breed || '').trim();
      if (!b) return;

      if (persist) core.setSelectedBreed(b);

      if (input) input.value = b;
      clearBtn?.classList.add('is-visible');
      listEl?.classList.remove('is-open');
      if (listEl) listEl.innerHTML = '';

      const { bedSizeKey, gearSizeKey, playSizeKey, sources } = await core.getProductsForBreed(b);

      log('Selected breed:', b);
      log('Breed->Size:', { breed: b, bedSizeKey, gearSizeKey, playSizeKey });

      if (!sources.length) {
        /* I18N-06 (#163) */
        setResultsEmpty(overlay, tr('no_products_breed', 'No products found for this breed.'));
        return;
      }

      setResultsLoading(overlay);

      try {
        const sourceResults = await Promise.all(
          sources.map(async (source) => {
            try {
              let products = await core.fetchCollectionProductsFallback(source.url, 30);

              products = products.map((p) => ({
                ...p,
                sourceKey: source.key,
                sourceLabel: source.label,
                recommendedSize: source.sizeKey,
              }));

              products = await core.enrichProductsWithSizeVariant(products, source.sizeKey);

              return products;
            } catch (sourceErr) {
              console.warn(NS, `Could not load source: ${source.key}`, sourceErr);
              return [];
            }
          })
        );

        const allProducts = sourceResults.flat();

        if (!allProducts.length) {
          /* I18N-06 (#163) */
          setResultsEmpty(overlay, tr('no_products_size', 'No products found for this breed size.'));
          return;
        }

        allProducts.sort((a, b) => {
          const sourceOrder = {
            beds: 1,
            gear: 2,
            toys: 3,
          };

          const aSourceOrder = sourceOrder[a.sourceKey] || 99;
          const bSourceOrder = sourceOrder[b.sourceKey] || 99;

          if (aSourceOrder !== bSourceOrder) {
            return aSourceOrder - bSourceOrder;
          }

          return String(a.title || '').localeCompare(String(b.title || ''), undefined, {
            sensitivity: 'base',
          });
        });

        renderResultsCards(overlay, allProducts);
      } catch (err) {
        console.error(NS, err);
        /* I18N-06 (#163) */
        setResultsEmpty(overlay, tr('load_error', 'Could not load products. Please try again.'));
      }
    }

    overlay.__applyBreed = applyBreed;

    closeBtn?.addEventListener('click', () => closeModal(overlay, core));

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) closeModal(overlay, core);
    });

    document.addEventListener('keydown', (e) => {
      if (!overlay.classList.contains('is-open')) return;
      /* A11Y-20 (#302): mark this Escape handled, so c-dialog.js does not also close a dialog open
         underneath (it skips a prevented one) */
      if (e.key === 'Escape') {
        e.preventDefault();
        closeModal(overlay, core);
      }
    });

    clearBtn?.addEventListener('click', () => {
      if (!input) return;

      core.removeSelectedBreed();

      input.value = '';
      clearBtn.classList.remove('is-visible');
      update();

      listEl?.classList.remove('is-open');
      if (listEl) listEl.innerHTML = '';

      /* I18N-06 (#163) */
      resetResults(overlay, tr('prompt', 'Choose a breed to see recommended products.'));

      input.focus();
    });

    input?.addEventListener('input', update);

    listEl?.addEventListener('click', async (e) => {
      const item = e.target.closest('.breed-modal__item');
      if (!item) return;

      const breed = item.getAttribute('data-breed') || '';
      if (!breed) return;

      await applyBreed(breed, { persist: true });
    });

    listEl?.addEventListener(
      'wheel',
      (e) => {
        if (!overlay.classList.contains('is-open')) return;
        const atTop = listEl.scrollTop <= 0;
        const atBottom = listEl.scrollTop + listEl.clientHeight >= listEl.scrollHeight - 1;
        if ((e.deltaY < 0 && atTop) || (e.deltaY > 0 && atBottom)) e.preventDefault();
      },
      { passive: false }
    );
  }

  function createUI(config, core) {
    const overlay = buildModalDOM({ heroImg: config.heroImg });

    /* I18N-06 (#163) */
    resetResults(overlay, tr('prompt', 'Choose a breed to see recommended products.'));

    const floatBtn = buildFloatingButton({
      floatBtnId: config.floatBtnId,
      iconUrl: config.floatIconUrl,
    });

    return {
      overlay,
      floatBtn,
      open: () => openModal(overlay, core),
      close: () => closeModal(overlay, core),
      bind: (breedsSorted) => bindEvents(overlay, core, breedsSorted),
    };
  }

  window.WOOPF_BREED_MODAL_UI = { createUI };
})();
